import axios from 'axios';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import LoaderComponent from 'components/LoaderComponent';
import LogoDark from 'LogoDark';
import moment from 'moment';
import React, { useEffect, useState } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Button, Form, Icon } from 'semantic-ui-react';
import { addSnapshotToDraft, getCdrImageUploadViewData } from 'services/cdrDocumentServices';
import styled from 'styled-components';
import { CdrImageUploadViewData } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledLogo = styled(LogoDark)`
  &.logo-icon {
    transform: scale(0.6);
    margin-top: 0.2rem;
    margin-left: -2.5rem;
    height: 2rem;
  }
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  margin: 0 1.5rem;

  .page-header {
    flex: 0 0 auto;
    border-bottom: 1px solid #2D404A;
    padding: 0.9rem 0;
    font-size: 1.4rem;
    margin: 0 0 2rem;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    .praxmedic-logo-container {
      flex: 1 1 auto;
      width: 2.675em;
      max-width: 9.675rem;
      height: 2.675rem;
    }

    .header-title {
      flex: 0 0 auto;
      line-height: 2.775rem;
      color: #0f1a52;
    }
  }

  .page-footer {
    flex: 0 0 auto;
    border-top: 1px solid #2D404A;
    padding: 0.35rem 1rem 0.5rem;
    font-size: 1.1rem;
    margin: 1.2rem 0 0;
    color: #0f1a52;

    .left {
      float: left;
    }

    .right {
      float: right;
    }
  }

  .result-form .error {
    margin: 0;
    padding: 0;
    background: none;
  }

  @media only screen and (min-width: 320px) and (max-width: 767px) {
    .page-header {
      margin-bottom: 0.3rem;
    }

    .page-footer {
      padding: 0.35rem 0.15rem 0.5rem;
      font-size: 1rem;
    }
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;

  .ui.button {
    margin-right: 0.75rem;
  }
`;

const StyledContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: start;
  flex-direction: column;
  max-width: 768px;

  .info-text {
    font-size: 1.2rem;
    margin-bottom: 2rem;
  }

  video {
    border: 2px solid;
  }

  .snapshot-container {
    display: flex;
    flex-direction: column;
    align-self: center;
    align-items: center;

    .image-input {
      display: none;
    }

    .snapshot-placeholder {
      display: flex;
      flex-direction: column;
      max-width: 80vh;
      min-width: 30vh;
      min-height: 20vh;
      align-items: center;
      border: 1px solid #e5e5e5;
      padding-top: 1rem;
      border-radius: .28571429rem;

      i.icon {
        color: #e5e5e5;
      }
    }

    .take-snapshot-action {
      display: flex;
      flex-direction: column;
      margin-top: 1rem;
      align-items: center;

      i.icon {
        color: #2185d0;
      }

      .action-label {
        color: #1678c2;
        font-size: 1.1rem;
      }
    }

    .image-box {
      max-width: 90%;
      max-height: 50vh;
      margin: 10px;
      text-align: center;

      img {
        height: inherit;
        max-height: inherit;
        max-width: inherit;
      }
    }
  }

  .result {
    margin: 1rem 0 2.5rem;
    font-size: 1.2rem;
    font-weight: 500;
  }

  .warning, .success, .error {
    border-radius: .28571429rem;
    padding: 1rem;
    font-size: 1.2rem;
    margin-bottom: .5rem;
    width: 100%;
  }

  .warning {
    box-shadow: 0 0 0 1px #c9ba9b inset, 0 0 0 0 transparent;
    background-color: #fffaf3;
    color: #573a08;
  }

  .success {
    box-shadow: 0 0 0 1px #a3c293 inset, 0 0 0 0 transparent;
    background-color: #fcfff5;
    color: #2c662d;
  }

  .error {
    box-shadow: 0 0 0 1px #e0b4b4 inset, 0 0 0 0 transparent;
    background-color: #fff6f6;
    color: #9f3a38;
  }

  .info-message {
    margin: 1rem 0;
    font-size: 1.2rem;
  }
`;

const StyledInfoRow = styled.div`
  background-color: ${(props: InfoRowProps) => props.backgroundColor || 'inherited'};
  width: 100%;
  display: flex;
  padding: 0.5rem;
  border-radius: .28571429rem;

  *:first-child {
    width: 150px;
  }

  *:nth-child(2) {
    flex: 1;
  }

  &.target-employee {
    margin-bottom: 1.5rem;
  }
`;

interface InfoRowProps {
  backgroundColor?: string;
}

interface Props extends MessageSourceProps,
    RouteComponentProps {
}

type PageState = "ENTRY" | "SUCCESS" | "ERROR" | "FILE_SIZE_EXCEEDED" | "INVALID_TOKEN";

const PatientDocumentImageUpload = (props: Props) => {

  const {getMessage, match} = props;

  const [pageState, setPageState] = React.useState("ENTRY" as PageState);
  const [dataLoading, setDataLoading] = useState(false);
  const [data, setData] = useState<CdrImageUploadViewData | undefined>();
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);
  const [fileDocumentSnapshot, setFileDocumentSnapshot] = useState(undefined);
  const [source, setSource] = useState("");

  const cancelTokenSource = axios.CancelToken.source();

  const takeSnapshotKey = source
      ? "patientDocumentSnapshot.action.retakeSnapshot"
      : "patientDocumentSnapshot.action.takeSnapshot";

  //@ts-ignore
  const token = match.params.token;

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true);
      try {
        const result = await getCdrImageUploadViewData(token, cancelTokenSource);
        setDataLoading(false);
        setData(result);
      } catch (e) {
        handleError(e.response.data);
      } finally {
        setDataLoading(false);
      }
    }

    fetchData();
  }, []);

  const handleError = (error: any) => {
    const knownErrors: Array<string> = [
      errorUtils.invalidToken,
      errorUtils.unknownEmployee
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode) && violation.errorCode === errorUtils.invalidToken) {
          setPageState("INVALID_TOKEN");
          setErrorMessage(getMessage(`error.${errorUtils.invalidToken}`));
        } else {
          setPageState("ERROR");
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      setPageState("ERROR");
      setErrorMessage(getMessage("error.generalErrorMessage"))
    }
  };

  const handleCapture = (target: any) => {
    if (target.files) {
      if (target.files.length !== 0) {
        const file = target.files[0];

        if (file.size <= 10485760) {
          setFileDocumentSnapshot(file);
          const newUrl = URL.createObjectURL(file);
          setSource(newUrl);
        } else {
          setPageState("FILE_SIZE_EXCEEDED");
        }
      }
    }
  };

  const onUploadTestResult = async () => {
    try {
      await addSnapshotToDraft(
          token,
          fileDocumentSnapshot,
          cancelTokenSource
      );

      setPageState("SUCCESS");
    } catch (e) {
      if (e.message === "Network Error") {
        setPageState("FILE_SIZE_EXCEEDED");
      } else {
        handleError(e.response.data);
      }
    }
  };

  const onUploadAgain = () => {
    setPageState("ENTRY");
    setFileDocumentSnapshot(undefined);
    setSource("");
  };

  const renderTopPageData = () => !!data && (
      <>
        <StyledInfoRow>
          <span>{getMessage("patientDocumentSnapshot.content.administrator")}</span>
          <span><b>{`${data!.userName} ${data!.userFirstName}`}</b></span>
        </StyledInfoRow>

        <h3>{getMessage("patientDocumentSnapshot.action.uploadSnapshot")}</h3>

        <StyledInfoRow className="target-employee" backgroundColor="#e5e5e5">
          <span>{getMessage("patientDocumentSnapshot.content.employee")}</span>
          <span><b>{`${data!.patientName} ${data!.patientFirstName},
      ${moment(data!.patientBirthDate).format("DD.MM.YYYY")}`}</b></span>
        </StyledInfoRow>
      </>
  );

  const renderFileSizeExceeded = () => (
      <>
        <div className="error">
          {getMessage("patientDocumentSnapshot.message.fileSizeExceeded")}
        </div>
        {renderTopPageData()}

        <div className="info-message">
          {getMessage("patientDocumentSnapshot.message.smallerImage")}
        </div>

        <StyledButtonContainer>
          <Button
              type="button"
              className="action-button"
              color="grey"
              onClick={() => setPageState("ENTRY")}
          >
            {getMessage('patientDocumentSnapshot.action.retakeSnapshot')}
          </Button>
        </StyledButtonContainer>
      </>
  );

  const renderSuccess = () => (
      <>
        <div className="success">
          {getMessage("patientDocumentSnapshot.message.success")}
        </div>
        {renderTopPageData()}
        <StyledButtonContainer>
          <Button
              type="button"
              className="action-button"
              primary
              compact
              onClick={onUploadAgain}
          >
            {getMessage('patientDocumentSnapshot.action.uploadAnother')}
          </Button>
        </StyledButtonContainer>
      </>
  );

  const renderError = () => (
      <>
        <div className="error">
          {errorMessage}
        </div>
        {pageState !== "INVALID_TOKEN" && renderTopPageData()}

        <div className="info-message">
          {getMessage("patientDocumentSnapshot.message.somethingWentWrong")}
        </div>

        {pageState !== "INVALID_TOKEN" &&
            <StyledButtonContainer>
              <Button
                  type="button"
                  className="action-button"
                  color="grey"
                  onClick={() => setPageState("ENTRY")}
              >
                {getMessage('patientDocumentSnapshot.action.retakeSnapshot')}
              </Button>
            </StyledButtonContainer>
        }
      </>
  );

  const renderEntryState = () => (
      <>
        {renderTopPageData()}

        <Form className="result-form">
          <div className="snapshot-container">
            <input
                accept="image/*"
                className="image-input"
                id="icon-button-file"
                type="file"
                capture="environment"
                onChange={(e) => handleCapture(e.target)}
            />

            <label htmlFor="icon-button-file">
              {
                source
                    ?
                    <div className="image-box">
                      <img src={source} alt={"snap"} className=""/>
                    </div>
                    :
                    <div className="snapshot-placeholder">
                      <Icon.Group size="huge">
                        <Icon name="camera"/>
                        <Icon corner="top right" name="file alternate outline"/>
                      </Icon.Group>
                    </div>
              }
            </label>

            <label htmlFor="icon-button-file">
              <div className="take-snapshot-action">
                <Icon name="camera" size="large"/>
                <div className="action-label">
                  {getMessage(takeSnapshotKey)}
                </div>
              </div>
            </label>
          </div>

          <StyledButtonContainer>
            <Button
                type="button"
                className="action-button"
                primary
                compact
                onClick={onUploadTestResult}
                disabled={!fileDocumentSnapshot}
            >
              {getMessage('patientDocumentSnapshot.action.upload')}
            </Button>
          </StyledButtonContainer>
        </Form>
      </>
  );

  return (
      <MainContainer>
        <div className="page-header">
          <div className="praxmedic-logo-container">
            <StyledLogo/>
          </div>
        </div>
        <div>
          <StyledContainer>
            {
              dataLoading
                  ? (
                      <StyledViewTableRow>
                        <LoaderComponent active/>
                      </StyledViewTableRow>
                  ) : (
                      <>
                        {
                            pageState === "ENTRY" &&
                            renderEntryState()
                        }
                        {
                            pageState === "SUCCESS" &&
                            renderSuccess()
                        }
                        {
                            pageState === "ERROR" &&
                            renderError()
                        }
                        {
                            pageState === "INVALID_TOKEN" &&
                            renderError()
                        }
                        {
                            pageState === "FILE_SIZE_EXCEEDED" &&
                            renderFileSizeExceeded()
                        }
                      </>
                  )
            }
          </StyledContainer>
        </div>
        <div className="page-footer">
          <div className="left">{getMessage("page.footer.copyright")}</div>
          <div className="right">{getMessage("page.footer.appName")}</div>
        </div>
      </MainContainer>
  );

};

export default withRouterWorkaround(withMessages(PatientDocumentImageUpload));
